<i18n>
{
  "en": {
    "product": "Menu product",
    "masterProduct": "master product",
    "deletedProduct": "Deleted master product",
    "specialDescription": "Special description",
    "specialDescriptionHint": "Special description when displayed in the menu",
    "special": "Show special product",
    "saleFormats": "Sales formats in use",
    "visible": "Product is visible",
    "create": "Add",
    "edit": "Edit",
    "showSpecialDescriptionCondition": "Show special description",
    "showVideoCondition": "Show video instead of image"
  },
  "es": {
    "product": "Producto en menú",
    "masterProduct": "producto maestro",
    "deletedProduct": "Producto maestro eliminado",
    "specialDescription": "Descripción especial",
    "specialDescriptionHint": "Descripción especial tal y como se ve en el menú",
    "special": "Mostrar que el producto es especial",
    "saleFormats": "Formatos de venta en uso",
    "visible": "El producto es visible",
    "create": "Añadir",
    "edit": "Editar",
    "showSpecialDescriptionCondition":  "Mostrar descripcion especial",
    "showVideoCondition": "Mostrar vídeo en lugar de imagen"
  }
}
</i18n>

<template>
  <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
    <v-card v-if="visible" height="100%" class="px-5 py-4">
      <v-card-title class="pl-0 pt-0">
        {{ modalTitle }}
      </v-card-title>
      <v-card-subtitle class="pl-0 pb-0">
        {{ product.name }}
      </v-card-subtitle>
      <v-card-actions class="px-0 pb-0">
        <v-btn small :disabled="isDeleted" @click="onEdit">{{ masterProductString }}</v-btn>
      </v-card-actions>
      <v-container class="pa-0">
        <v-form v-model="valid" ref="form">
          <v-switch v-if="showSpecial" v-model="special" :disabled="product.is_managed"
                    :label="$t('special')" required/>
          <v-switch v-if="showSpecialDescription" v-model="specialDescriptionCondition"
                    :label="$t('showSpecialDescriptionCondition')" required/>
          <v-switch v-if="showUseVideoCondition" v-model="useVideoCondition"
                    :label="$t('showVideoCondition')" required/>
          <DynameatLangugageFieldGenerator
            v-model="specialDescriptions"
            v-if="specialDescriptionCondition"
            :label="$t('specialDescription')"
            :type="'textField'"
            :hint="$t('specialDescriptionHint')"
            :rules="longTextRules"
          />
          <v-switch v-model="isVisible" :label="$t('visible')" required/>
          <v-select v-model="allowedSaleFormats" :items="filteredMasterSaleFormatList"
                    :product-props="{ maxHeight: '800' }" :label="$t('saleFormats')"
                    item-value="guid" item-text="title" chips multiple required/>
          <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm" :confirmDisabled="!valid"
                                  :confirmText="$t('confirm')"/>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import { Prop, Watch } from 'vue-property-decorator';
import Component from 'vue-class-component';
import Vue from 'vue';
import { Components, MapGetters } from '@core/helpers/decorators';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import { maxChars } from '@core/helpers/formValidators';
import DynameatLangugageFieldGenerator from '@dynameat/components/DynameatLanguageFieldGenerator';
import { ProductItem } from '../models';

@Component
@Components({ DynameatModalButtons, DynameatLangugageFieldGenerator })
@MapGetters(['masterSaleFormatList', 'currentSection', 'currentMenu', 'currentTemplate',
  'masterProductList', 'pointOfSaleLanguages'])
export default class ProductItemCreateEditModal extends Vue {
  @Prop({ String, default: 'create', validator(type) { return type === 'create' || type === 'edit'; } }) type;

  @Prop({ Number, default: 0, }) newOrder;

  @Prop({ Object, validator(product) { return product instanceof ProductItem; }, }) product;

  @Prop({ Boolean, default: false }) visible;

  @Prop({ Boolean, default: false }) isManaged;

  valid = false;

  productGuid = '';

  special = false;

  specialDescriptionCondition = false;

  useVideoCondition = false;

  specialDescriptions = {};

  allowedSaleFormats = [];

  isVisible = true;

  isDeleted = false;

  get modalTitle() {
    return `${this.$t(this.type)} ${this.$t('product')}`;
  }

  get masterProductString() {
    return this.isDeleted ? this.$t('deletedProduct') : `${this.$t('edit')} ${this.$t('masterProduct')}`;
  }

  get masterProduct() {
    return this.masterProductList.find((el) => el.guid === this.product.product);
  }

  get filteredMasterSaleFormatList() {
    const { masterProduct } = this;
    if (masterProduct) {
      const childFormats = this.masterSaleFormatList.filter((el) => Object.keys(masterProduct.sale_formats).includes(el.guid));
      const masterFormats = this.masterSaleFormatList.filter((el) => el.guid === masterProduct.master_sale_format);
      return [...masterFormats, ...childFormats];
    }
    return [];
  }

  get showSpecial() {
    return !this.currentTemplate.isOperationDisallowed('PRODUCT', 'SPECIAL');
  }

  get showSpecialDescription() {
    return !this.currentTemplate.isOperationDisallowed('PRODUCT', 'SPECIAL_DESCRIPTION');
  }

  get showUseVideoCondition() {
    return !this.currentTemplate.isOperationDisallowed('PRODUCT', 'VIDEO');
  }

  get showSpecialFrame() {
    return !this.currentTemplate.isOperationDisallowed('PRODUCT', 'SPECIAL_FRAME');
  }

  get longTextRules() {
    return {
      alt1: [maxChars(this.$t('tooLongText'), 256)],
      alt2: [maxChars(this.$t('tooLongText'), 256)],
      alt3: [maxChars(this.$t('tooLongText'), 256)]
    };
  }

  get availableLanguages() {
    return this.pointOfSaleLanguages;
  }

  @Watch('product')
  onUpdatedProduct(updatedProduct) {
    if (updatedProduct) {
      this.productGuid = updatedProduct.product;
      this.special = updatedProduct.special;
      this.specialFrame = updatedProduct.special;
      this.specialDescriptionCondition = updatedProduct.is_special_description;
      this.useVideoCondition = updatedProduct.use_video;
      this.availableLanguages.forEach((el) => {
        this.specialDescriptions[el.alternative] = updatedProduct[`special_description_${el.alternative}`];
      });
      this.allowedSaleFormats = updatedProduct.allowed_sale_formats;
      this.isVisible = updatedProduct.is_visible;
      this.isDeleted = updatedProduct.is_deleted;
      // Add default sale formats when creating a new product
      if (this.allowedSaleFormats.length === 0 && this.type === 'create') {
        this.allowedSaleFormats = this.currentMenu.default_sale_formats;
      }
    }
  }

  onCancel() {
    this.$emit('cancel');
  }

  onEdit() {
    this.$emit('edit-master', this.product.product);
  }

  onConfirm() {
    if (this.$refs.form.validate()) {
      this.product.product = this.product.product || this.productGuid;
      this.product.section = this.product.section || this.currentSection.guid;
      if (this.type === 'create') this.product.default_order = this.newOrder;
      this.product.special = this.special;
      this.product.special_frame = this.special;
      this.product.is_special_description = this.specialDescriptionCondition;
      this.product.use_video = this.useVideoCondition;
      this.availableLanguages.forEach((el) => {
        this.product[`special_description_${el.alternative}`] = this.specialDescriptions[el.alternative];
      });
      this.product.allowed_sale_formats = this.allowedSaleFormats;
      this.product.is_visible = this.isVisible;
      this.$emit('confirm', this.product);
    }
    if (this.product.is_managed) {
      this.$emit('added-managed', this.product.section);
    }
  }
}
</script>
<style scoped>
.required label::after {
  content: " *";
  color: red;
}
</style>
